import {
  Box,
  Container,
  Group,
  Text,
  ThemeIcon,
  UnstyledButton,
} from '@mantine/core'
import { IconWorld } from '@tabler/icons-react'
import { useLingui } from '@lingui/react'
import { useLingui as useLinguiMacro } from '@lingui/react/macro'
import { notifications } from '@mantine/notifications'
import { useTurnstile } from '@markku/components'
import { useSearch } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { Locale, locales, setLocale } from '$src/lib/i18n'
import { EmailStep } from './EmailStep'
import { OtpStep } from './OtpStep'
import { WelcomeStep } from './WelcomeStep'

export function WelcomeLogin() {
  const { i18n } = useLingui()
  const { t } = useLinguiMacro()
  const [step, setStep] = useState<'welcome' | 'email' | 'otp'>('welcome')
  const [email, setEmail] = useState('')
  const [turnstileToken, setTurnstileToken] = useState<string>('')
  const { redirect } = useSearch({ from: '/' })

  const { containerRef: turnstileRef, reset: resetTurnstile } = useTurnstile({
    sitekey: import.meta.env.VITE_TURNSTILE_SITE_KEY || '',
    onSuccess: (token) => {
      setTurnstileToken(token)
    },
    onError: () => {
      setTurnstileToken('')
    },
  })

  // Show notification if redirected due to auth requirement (but not for /home - that's just logout)
  useEffect(() => {
    if (redirect && redirect !== '/home') {
      notifications.show({
        id: 'login-required',
        title: t`Login required`,
        message: t`You must log in to continue.`,
        color: 'yellow',
      })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleEmailSuccess = (emailValue: string) => {
    setEmail(emailValue)
    setStep('otp')
  }

  const handleBack = () => {
    if (step === 'otp') {
      setStep('email')
    } else if (step === 'email') {
      setStep('welcome')
    }
  }

  const handleRetry = () => {
    setStep('email')
    resetTurnstile()
  }

  return (
    <Box
      style={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
        position: 'relative',
      }}
    >
      <Group
        gap="xs"
        style={{ position: 'absolute', top: '1rem', right: '1rem' }}
      >
        <ThemeIcon variant="subtle" color="gray" size="md">
          <IconWorld size={18} />
        </ThemeIcon>
        {(Object.keys(locales) as Locale[]).map((locale, index) => (
          <Group key={locale} gap={6}>
            {index > 0 && (
              <Text c="dimmed" size="md">
                ·
              </Text>
            )}
            <UnstyledButton onClick={() => setLocale(locale)}>
              <Text
                size="md"
                fw={i18n.locale === locale ? 600 : 400}
                c={i18n.locale === locale ? 'dark' : 'dimmed'}
                style={{ transition: 'all 0.2s' }}
              >
                {locales[locale]}
              </Text>
            </UnstyledButton>
          </Group>
        ))}
      </Group>
      <Container size={420} p={0}>
        {step === 'welcome' && (
          <WelcomeStep onContinue={() => setStep('email')} />
        )}

        {step === 'email' && (
          <EmailStep
            turnstileRef={turnstileRef}
            turnstileToken={turnstileToken}
            resetTurnstile={resetTurnstile}
            setTurnstileToken={setTurnstileToken}
            onBack={handleBack}
            onSuccess={handleEmailSuccess}
          />
        )}

        {step === 'otp' && (
          <OtpStep email={email} onBack={handleBack} onRetry={handleRetry} />
        )}
      </Container>
    </Box>
  )
}
